import React, { useState, useEffect, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { SectionTitle } from "@/components/SectionTitle";
import { toast } from "sonner";
import { CheckCircle } from "@phosphor-icons/react";
import { PaymentInfo } from "@/components/PaymentInfo";

const COURT = "https://images.unsplash.com/photo-1552984418-b55abf780dd4?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

export default function Register() {
  const { settings } = useSettings();
  const DIVISIONS = useMemo(() => settings.divisions || [], [settings.divisions]);
  const [type, setType] = useState("player");
  const [form, setForm] = useState({ name: "", email: "", phone: "", team_name: "", division: "Open", position: "", message: "" });
  const [roster, setRoster] = useState([{ name: "", number: "", position: "" }]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const setRosterField = (i, k, v) => setRoster((prev) => prev.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRosterRow = () => setRoster((prev) => [...prev, { name: "", number: "", position: "" }]);
  const removeRosterRow = (i) => setRoster((prev) => prev.filter((_, idx) => idx !== i));

  useEffect(() => {
    if (DIVISIONS.length && !DIVISIONS.includes(form.division)) setForm((f) => ({ ...f, division: DIVISIONS[0] }));
  }, [DIVISIONS, form.division]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Open the payment tab synchronously (within the user gesture) to avoid popup blockers.
    const payEnabled = !!(settings.payments && settings.payments.enabled);
    const payWin = payEnabled ? window.open("/pay", "_blank") : null;
    try {
      const cleanRoster = type === "team" ? roster.filter((r) => r.name.trim()).map((r) => ({ name: r.name.trim(), number: String(r.number || ""), position: r.position || "" })) : [];
      await api.post("/registrations", { ...form, type, roster: cleanRoster });
      setSubmitted(true);
      toast.success("Registration submitted!");
    } catch (err) {
      if (payWin) payWin.close();
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-28 text-center" data-testid="register-success">
        <CheckCircle size={72} weight="fill" className="text-[#00E676] mx-auto mb-6" />
        <h2 className="font-display text-5xl uppercase">You're on the list!</h2>
        <p className="text-white/60 mt-4">Thanks for registering. Our league admins will review your submission and reach out shortly.</p>
        <div className="text-left"><PaymentInfo payments={settings.payments} /></div>
        <button
          data-testid="register-another-btn"
          onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", team_name: "", division: "Open", position: "", message: "" }); setRoster([{ name: "", number: "", position: "" }]); }}
          className="mt-8 bg-[#FF5722] hover:bg-[#FF7043] text-black font-bold uppercase tracking-wide px-8 py-3 transition-all duration-200"
        >
          Submit Another
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[80vh]">
      <div className="relative hidden lg:block">
        <img src={COURT} alt="court" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="relative h-full flex items-end p-12">
          <h2 className="font-display text-6xl uppercase leading-[0.85]">Join the<br /><span className="text-[#FF5722]">league.</span></h2>
        </div>
      </div>

      <div className="px-6 sm:px-12 py-16 max-w-xl mx-auto w-full">
        <SectionTitle overline="Sign up" title="Registration" sub="Register as an individual player or sign up your entire team for the upcoming season." />

        <div className="flex gap-2 mb-8">
          {["player", "team"].map((t) => (
            <button
              key={t}
              data-testid={`regtype-${t}`}
              onClick={() => setType(t)}
              className={`px-6 py-2.5 text-sm font-bold uppercase tracking-wide transition-all duration-200 ${
                type === t ? "bg-[#FF5722] text-black" : "border border-white/15 text-white/70 hover:text-white"
              }`}
            >
              {t === "player" ? "Individual Player" : "Team"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="registration-form">
          <Field label={type === "team" ? "Captain / Contact Name" : "Full Name"} required value={form.name} onChange={set("name")} testid="reg-name" />
          <Field label="Email" type="email" required value={form.email} onChange={set("email")} testid="reg-email" />
          <Field label="Phone" value={form.phone} onChange={set("phone")} testid="reg-phone" />
          <Field label="Team Name" value={form.team_name} onChange={set("team_name")} testid="reg-team" />
          <div>
            <label className="block text-sm font-semibold uppercase tracking-wide text-white/70 mb-2">Division <span className="text-[#FF5722]">*</span></label>
            <select
              data-testid="reg-division"
              required
              value={DIVISIONS.includes(form.division) ? form.division : (DIVISIONS[0] || "")}
              onChange={set("division")}
              className="w-full bg-[#1A1A1A] border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all"
            >
              {DIVISIONS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          {type === "player" && (
            <Field label="Position" placeholder="e.g. Point Guard" value={form.position} onChange={set("position")} testid="reg-position" />
          )}
          {type === "team" && (
            <RosterBuilder roster={roster} setRosterField={setRosterField} addRosterRow={addRosterRow} removeRosterRow={removeRosterRow} />
          )}
          <div>
            <label className="block text-sm font-semibold uppercase tracking-wide text-white/70 mb-2">Message</label>
            <textarea
              data-testid="reg-message"
              value={form.message}
              onChange={set("message")}
              rows={3}
              className="w-full bg-[#1A1A1A] border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="registration-submit-button"
            className="w-full bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-4 transition-all duration-200"
          >
            {loading ? "Submitting..." : "Submit Registration"}
          </button>
        </form>
        <PaymentInfo payments={settings.payments} />
      </div>
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-sm font-semibold uppercase tracking-wide text-white/70 mb-2">
        {label} {required && <span className="text-[#FF5722]">*</span>}
      </label>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-[#1A1A1A] border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all"
      />
    </div>
  );
}

function RosterBuilder({ roster, setRosterField, addRosterRow, removeRosterRow }) {
  return (
    <div data-testid="reg-roster">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-semibold uppercase tracking-wide text-white/70">Team Roster</label>
        <span className="text-xs text-white/40">{roster.filter((r) => r.name.trim()).length} players</span>
      </div>
      <div className="space-y-2">
        {roster.map((r, i) => (
          <div key={i} className="flex gap-2 items-center" data-testid={`roster-row-${i}`}>
            <input
              value={r.number}
              onChange={(e) => setRosterField(i, "number", e.target.value)}
              placeholder="#"
              data-testid={`roster-number-${i}`}
              className="w-14 bg-[#1A1A1A] border border-white/15 px-2 py-2.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all"
            />
            <input
              value={r.name}
              onChange={(e) => setRosterField(i, "name", e.target.value)}
              placeholder="Player name"
              data-testid={`roster-name-${i}`}
              className="flex-1 bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all"
            />
            <input
              value={r.position}
              onChange={(e) => setRosterField(i, "position", e.target.value)}
              placeholder="Position"
              data-testid={`roster-position-${i}`}
              className="w-28 bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all"
            />
            {roster.length > 1 && (
              <button type="button" onClick={() => removeRosterRow(i)} data-testid={`roster-remove-${i}`} className="text-white/40 hover:text-[#FF3B30] px-1 transition-colors">✕</button>
            )}
          </div>
        ))}
      </div>
      <button type="button" onClick={addRosterRow} data-testid="roster-add-btn" className="mt-3 border border-white/20 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 text-xs font-bold uppercase tracking-wide px-4 py-2 transition-all duration-200">
        + Add Player
      </button>
    </div>
  );
}
